import { type ConferencePage } from '@/lib/conferenceStructuredData.types';
import { Footer, Header } from '@ingeniorforeningen/figurine-cms';
import { AppShell } from '@ingeniorforeningen/figurine-core';
import {
  IApiContentResponseModel,
  IApiMediaWithCropsResponseModel,
  TEnv,
} from '@ingeniorforeningen/umbraco-client';
import Head from 'next/head';
import NextImage from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React from 'react';
import bg from '../../public/assets/images/preview.png';
import ConferenceJsonLd, { getAbsoluteSiteUrl } from '../components/ConferenceJsonLd';
import Preview from '../components/Preview';
import { layoutMap } from './templates';
import DefaultPageLayout from './templates/DefaultPageLayout';

type Props = {
  children: React.ReactNode;
  headerData?: IApiContentResponseModel;
  footerData?: IApiContentResponseModel;
  image?: IApiMediaWithCropsResponseModel;
  page?: IApiContentResponseModel;
  conferenceJsonLdData?: ConferencePage[];
};

const Layout = ({ children, headerData, footerData, page, image, conferenceJsonLdData }: Props) => {
  const { asPath, locale, isPreview } = useRouter();
  const { id, properties: pageProperties, contentType } = page || {};
  const { properties: headerProperties } = headerData || {};
  const { signUpLink, programLink, isActive } = headerProperties || {};

  const properties = {
    ...pageProperties,
    isActive,
    signUpLink: isActive ? signUpLink : undefined,
    programLink: isActive ? programLink : undefined,
    meetupNumberOnline: headerProperties?.meetupNumberOnline,
    meetupNumberPhysical: headerProperties?.meetupNumberPhysical,
  } as Record<string, any>;
  const pagePath = asPath.split(/[?#]/)[0];
  const canonicalUrl = getAbsoluteSiteUrl(
    pagePath,
    process.env.NEXT_PUBLIC_IDA_DOMAIN || 'https://ida.dk',
    locale,
  );

  const PageLayout =
    (contentType && layoutMap[contentType] && layoutMap[contentType]) || DefaultPageLayout;

  function calculateSoMeImageUrl(): string {
    let suffix = '?format=jpg';
    let url = image?.url;
    let focal = image?.focalPoint;
    if (
      Array.isArray(properties?.metaImage) &&
      properties?.metaImage?.length > 0 &&
      properties?.metaImage[0]?.url
    ) {
      url = properties.metaImage[0].url;
      focal = properties.metaImage[0].focalPoint;
    } else if (properties?.image?.length > 0 && properties?.image[0]?.url) {
      url = properties.image[0].url;
      focal = properties.image[0].focalPoint;
    }
    if (focal) {
      suffix += `&rxy=${focal.left},${focal.top}`;
    }
    return url + suffix;
  }

  const HeaderDataWithRemovedLinkHosts = headerData
    ? JSON.parse(JSON.stringify(headerData).replace(/https:\/\/ci.ida.dk/g, ''))
    : [];

  const NextReplaceLink = ({ href, ...rest }: any) => {
    if (!href) {
      return null;
    }
    return <NextLink href={href} {...rest} replace />;
  };

  const isFrontpage = contentType === 'conferences_frontpage';

  return (
    <AppShell
      theme="conferences"
      clean
      scrollToTop={false}
      polymorphic={{
        link: NextReplaceLink,
        image: NextImage,
      }}
      language={locale as 'da' | 'en'}
      environmentVariables={{ env: process.env.NEXT_PUBLIC_ENV as TEnv }}
    >
      <>
        <Head>
          <meta charSet="utf-8" />
          <title>{(properties?.metaTitle ?? properties?.header) as string}</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta data-guid={id} />
          <meta name="description" content={properties?.metaDescription as string} />
          {((properties?.excludeFromSearchEngines as boolean) || !isFrontpage) && (
            <meta name="robots" content="noindex, follow" />
          )}
          <meta data-pagetype={contentType as string} />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />

          <meta
            property="og:title"
            content={
              (properties?.socialMediaTitle ??
                properties?.metaTitle ??
                properties?.header) as string
            }
          />
          <meta
            property="og:description"
            content={properties?.socialMediaTeaser ?? properties?.teaser?.toString()}
          />
          <meta property="og:image" content={calculateSoMeImageUrl()} />
        </Head>
        {isFrontpage && (
          <ConferenceJsonLd
            conferenceJsonLdData={conferenceJsonLdData}
            locale={locale}
            pagePath={pagePath}
            properties={properties}
          />
        )}
        <Header data={HeaderDataWithRemovedLinkHosts} selectedSite="IDA.DK" showUserLogin={false} />
        {isPreview && (
          <>
            <Preview />
          </>
        )}
        <div style={isPreview ? { backgroundImage: `url(${bg.src})` } : undefined}>
          <PageLayout properties={properties} image={image}>
            {children}
          </PageLayout>
        </div>
        <Footer data={footerData} />
      </>
    </AppShell>
  );
};

export default Layout;
